import { type ApiInterceptor } from "@modules/core/api/interceptors/apiInterceptor"
import { ConfigService } from "@modules/core/services/configService"
import { LoggerType } from "@modules/utils/loggerUtils"
import * as Sentry from "@sentry/react"
import { type AxiosResponse, isAxiosError } from "axios"

const MAX_BODY_LENGTH = 500

function truncateBody(data: unknown): string {
  try {
    const str = typeof data === "string" ? data : JSON.stringify(data ?? null)
    if (str.length <= MAX_BODY_LENGTH) return str
    return str.slice(0, MAX_BODY_LENGTH) + "...[truncated]"
  } catch {
    return "[unable to serialize]"
  }
}

/** Path only (no query) for stable Sentry grouping. */
function pathOnly(url: string): string {
  try {
    const path = url.startsWith("http") ? new URL(url).pathname : url.split("?")[0]
    return path || url
  } catch {
    return url.split("?")[0] ?? url
  }
}

export const responseLoggerInterceptor = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRootPath?: (data: any) => any,
): ApiInterceptor<AxiosResponse> => ({
  onRejected: (error) => {
    if (isAxiosError(error)) {
      const url = error.config?.url ?? "unknown"
      const method = error.config?.method?.toUpperCase() ?? "unknown"
      const status = error.response?.status
      const body = truncateBody(error.response?.data)
      const path = pathOnly(url)

      const responseMessage = (error.response?.data as { error?: { message?: unknown } } | undefined)?.error?.message
      console.warn(
        LoggerType.ApiError + `request ${method} ${url} failed with error`,
        JSON.stringify(responseMessage !== undefined ? responseMessage : error),
      )

      if (error.code === "ERR_NETWORK" || error.code === "ECONNABORTED") {
        const rejectNetworkError = error instanceof Error ? error : new Error(String(error))
        return Promise.reject(rejectNetworkError)
      }

      const summaryMessage = `API Error: ${method} ${path} ${String(status ?? "no status")}`
      const apiError = new Error(summaryMessage) as Error & { cause?: unknown }
      apiError.cause = error

      Sentry.withScope((scope) => {
        scope.setFingerprint(["api-error", method, path, String(status ?? "unknown")])
        scope.setLevel(status && status >= 500 ? "error" : "warning")
        scope.setTag("api_error", "true")
        scope.setTag("method", method)
        scope.setTag("status", String(status ?? "unknown"))
        scope.setTag("endpoint", path)
        scope.setContext("apiError", {
          endpoint: url,
          method,
          responseStatus: status,
          responseBody: body,
        })
        scope.setContext("originalError", {
          message: error.message,
          code: error.code,
        })
        Sentry.captureException(apiError)
      })
    } else {
      console.warn(LoggerType.ApiError + "request failed with error", error)
    }

    const rejectError = error instanceof Error ? error : new Error(String(error))
    return Promise.reject(rejectError)
  },
  onFulfilled: (response) => {
    const startRequestTime = (response.config as { metadata?: { startTime?: number } }).metadata?.startTime
    if (ConfigService.config.HIDE_BODY_REQUEST_LOG !== "true") {
      console.info(
        LoggerType.ApiSuccess +
          `request ${response.config.method?.toUpperCase() ?? ""} ${response.config.url ?? ""} succeeded ${getDuration(startRequestTime, Date.now()) ?? ""}`,
      )
    }

    if (ConfigService.config.HIDE_BODY_REQUEST_LOG !== "true") {
      console.debug(
        LoggerType.ApiSuccess + "with response",
        JSON.stringify(getRootPath ? getRootPath(response.data) : response.data),
      )
    }
    return response
  },
})

const getDuration = (start: number | undefined, end: number) => {
  if (start) {
    return `(${String(end - start)} ms)`
  }
}
